import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { TreeStructure, FloppyDisk } from "@phosphor-icons/react";
import LoadFailedPanel from "@/components/LoadFailedPanel";

export default function CaseLifecycles() {
  const { user } = useAuth();
  const canMutate = (user?.ux_tier || 0) >= 4;
  const [list, setList] = useState([]);
  const [active, setActive] = useState(null);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");
  const [loadErr, setLoadErr] = useState(null);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    setLoadErr(null);
    try {
      const { data } = await api.get("/lifecycles");
      setList(data.lifecycles || []);
      if (!active && data.lifecycles?.length) {
        setActive(data.lifecycles[0].code);
        setEditing(JSON.parse(JSON.stringify(data.lifecycles[0])));
      }
      } catch (e) { setLoadErr(e); }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (active) { const lc = list.find(l => l.code === active); if (lc) setEditing(JSON.parse(JSON.stringify(lc))); } }, [active, list]);

  const save = async () => {
    setErr(""); setMsg("");
    try {
      await api.put(`/lifecycles/${editing.code}`, { steps: editing.steps, label: editing.label, description: editing.description });
      setMsg(`Saved ${editing.code} — ${editing.steps.length} steps.`);
      await load();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || "Save failed."); }
  };

  const addStep = () => {
    setEditing({...editing, steps: [...editing.steps, { step_id: "new_" + Date.now(), name: "New step", description: "", owner_tier: 2, due_offset_days: 7, channel: "in_app", optional: false, order: editing.steps.length + 1 }]});
  };
  const removeStep = (i) => setEditing({...editing, steps: editing.steps.filter((_, idx) => idx !== i)});
  const updStep = (i, k, v) => {
    const copy = [...editing.steps];
    copy[i] = {...copy[i], [k]: k === "owner_tier" || k === "due_offset_days" ? parseInt(v) : v};
    setEditing({...editing, steps: copy});
  };
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= editing.steps.length) return;
    const copy = [...editing.steps];
    [copy[i], copy[j]] = [copy[j], copy[i]];
    setEditing({...editing, steps: copy});
  };

  return (
    <div data-testid="lifecycles-page" className="p-6 max-w-6xl space-y-5">
      {loadErr && <LoadFailedPanel error={loadErr} resource="Case Lifecycles" onRetry={load} />}
      <h1 className="text-2xl font-bold text-[#263776] flex items-center gap-2">
        <TreeStructure size={28} className="text-[#F47D20]" />
        Case Lifecycle Library
      </h1>
      <p className="text-xs text-[var(--text-muted)]">13 lifecycle codes — edit step sequence without code deployment. Tier 4+ to save.</p>
      <div className="flex flex-wrap gap-2">
        {list.map(lc => (
          <button key={lc.code} data-testid={`lifecycle-tab-${lc.code}`} onClick={() => setActive(lc.code)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${active === lc.code ? "bg-[#263776] text-white border-[#263776]" : "bg-white text-[var(--text-secondary)] border-[var(--border)] hover:border-[#263776]"}`}>
            {lc.code} · {lc.label}
          </button>
        ))}
      </div>
      {editing && (
        <div className="bg-white border border-[var(--border)] rounded p-5 space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xl font-bold">{editing.code} · {editing.label}</div>
              <div className="text-xs text-[var(--text-muted)]">{editing.description}</div>
            </div>
            {canMutate && (
              <button data-testid="lifecycle-save" onClick={save}
                className="bg-[#F47D20] text-white px-4 py-2 text-sm rounded hover:bg-[#c96009] flex items-center gap-2">
                <FloppyDisk weight="bold" /> Save
              </button>
            )}
          </div>
          {err && <div data-testid="lifecycle-err" className="bg-rose-50 border border-rose-200 text-rose-700 text-xs px-3 py-2 rounded">{err}</div>}
          {msg && <div data-testid="lifecycle-msg" className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs px-3 py-2 rounded">{msg}</div>}
          <div className="space-y-2">
            {editing.steps.map((s, i) => (
              <div key={s.step_id} data-testid={`lifecycle-step-${i}`} className="grid grid-cols-12 gap-2 items-center bg-[var(--surface-2)] px-3 py-2 rounded">
                <div className="col-span-1 text-center text-xs font-bold text-[var(--text-muted)]">#{i+1}</div>
                <input data-testid={`lifecycle-step-name-${i}`} value={s.name} onChange={e => updStep(i,"name",e.target.value)} disabled={!canMutate}
                  className="col-span-4 border border-[var(--border-strong)] rounded px-2 py-1 text-sm bg-white" />
                <input data-testid={`lifecycle-step-desc-${i}`} value={s.description||""} onChange={e => updStep(i,"description",e.target.value)} placeholder="description" disabled={!canMutate}
                  className="col-span-3 border border-[var(--border-strong)] rounded px-2 py-1 text-sm bg-white" />
                <select data-testid={`lifecycle-step-owner-${i}`} value={s.owner_tier} onChange={e => updStep(i,"owner_tier",e.target.value)} disabled={!canMutate}
                  className="col-span-1 border border-[var(--border-strong)] rounded px-2 py-1 text-xs bg-white">
                  {[1,2,3,4,5].map(t => <option key={t} value={t}>T{t}</option>)}
                </select>
                <input data-testid={`lifecycle-step-due-${i}`} type="number" value={s.due_offset_days} onChange={e => updStep(i,"due_offset_days",e.target.value)} disabled={!canMutate}
                  className="col-span-1 border border-[var(--border-strong)] rounded px-2 py-1 text-xs bg-white" />
                {canMutate && (
                  <div className="col-span-2 flex gap-1 justify-end">
                    <button onClick={() => move(i,-1)} className="text-[var(--text-faint)] hover:text-[#263776] px-1">↑</button>
                    <button onClick={() => move(i,+1)} className="text-[var(--text-faint)] hover:text-[#263776] px-1">↓</button>
                    <button data-testid={`lifecycle-step-remove-${i}`} onClick={() => removeStep(i)} className="text-rose-500 hover:text-rose-700 px-1">×</button>
                  </div>
                )}
              </div>
            ))}
            {canMutate && (
              <button data-testid="lifecycle-add-step" onClick={addStep}
                className="text-xs px-3 py-1.5 rounded border border-dashed border-[var(--border-strong)] text-[var(--text-muted)] hover:border-[#263776] hover:text-[#263776]">
                + Add step
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
